import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { notFoundUnlessRedirected } from "@/lib/slugGuards";
import { seoHead } from "@/config/seoHead";
import { buildTitle } from "@/config/seoTitles";
import { templateTitle } from "@/config/seoTitleTemplates";

const OrdlistaTerm = lazy(() => import("@/pages/info/OrdlistaTerm"));

export const Route = createFileRoute("/ordlista/$term")({
  // Ordlistan (med fördjupningsartiklar) laddas här i stället för i huvudpaketet.
  // Termen skickas med i loaderdatan, så sidkomponenten behöver inte importera
  // hela ordlistan (~130 kB) i webbläsaren.
  loader: async ({ params }) => {
    const { GLOSSARY, glossaryBySlug } = await import("@/data/glossary");
    const info = glossaryBySlug(params.term) ?? null;
    // Okänt begrepp: riktig 404 i stället för en tom termsida.
    if (!info) await notFoundUnlessRedirected(`/ordlista/${params.term}`);
    const related = info
      ? GLOSSARY.filter((t) => t.category === info.category && t.slug !== info.slug)
          .slice(0, 6)
          .map((t) => ({ label: t.term, to: `/ordlista/${t.slug}` }))
      : [];
    return { info, related };
  },
  head: ({ loaderData }) => {
    const info = loaderData?.info;
    if (!info) return seoHead("/ordlista/$term");

    const title = info.seoTitle
      ? `${info.seoTitle} | Elmarknad.se`
      : templateTitle(`/ordlista/${info.slug}`, info.term) ?? buildTitle(info.term, "term");

    return seoHead(`/ordlista/${info.slug}`, {
      title,
      description: info.seoDescription ?? info.short,
      ogType: "article",
    });
  },
  component: TermRoute,
});

function TermRoute() {
  const { info, related } = Route.useLoaderData();
  return <OrdlistaTerm info={info} related={related} />;
}
